import { Fragment, useContext, useEffect, useState } from 'react'

import classNames from 'classnames'
import { forEach, isNull, map } from 'lodash'
import TagManager from 'react-gtm-module'

import { useMutation } from '@apollo/client'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faChevronDown,
  faChevronUp,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans } from '@lingui/macro'
import Button from 'Root/App/Button'
import CreateContent from 'Root/App/Button/CreateContent'
import Input from 'Root/App/Form/Input'
import PreTitle from 'Root/App/PreTitle'
import Title from 'Root/App/Title'
import { Context as AssociationContext } from 'Root/Pages/Collection/Component/AssociationContext'
import Item from 'Root/Pages/Collection/Component/Contents/Item'
import SortableWrapper from 'Root/Pages/Collection/Component/Contents/SortableWrapper'
import mixpanelTrack from 'lib/mixpanel-track'
import { secondsToDuration } from 'lib/seconds-to-duration'
import { useFeatureEnabledByDate } from 'lib/use-feature-enabled-by-date'

import ModuleMenu from './ModuleMenu'
import styles from './index.module.css'
import REMOVEALL from './remove-module-and-contents.graphql'
import REMOVE from './remove-module.graphql'
import MUTATION from './update-module.graphql'

// ----------------------------------------------------------------------------

library.add(faChevronUp)
library.add(faChevronDown)
library.add(faXmark)

// ----------------------------------------------------------------------------

const ModuleInfo = ({
  module,
  canManage,
  canReadCollection,
  refetchOnUpdate,
  visibility,
  collectionId,
  collectionSlug,
  collectionName = '',
  openOnStart,
}) => {
  const { setCollectionModule } = useContext(AssociationContext)
  const [loading, featureIsEnabledForThisUser] = useFeatureEnabledByDate()

  const [isModuleOpen, setIsModuleOpen] = useState(null)
  const [completed, setCompleted] = useState(0)

  const [mutate] = useMutation(MUTATION)
  const [removeAll] = useMutation(REMOVEALL)
  const [remove] = useMutation(REMOVE)

  const [title, setTitle] = useState(module.title ?? '')
  const [showLength, setShowLength] = useState(false)

  const contents = map(module?.contents?.edges || [], 'node')

  const MAXLENGTH = '65'

  // -------------------------------------

  useEffect(() => {
    if (!!contents && isNull(isModuleOpen)) {
      let completed = 0
      forEach(contents, (content) => {
        if (content.hasUserVisited !== undefined && content.hasUserVisited) {
          completed++
        }
        if (
          content.hasUserCompleted !== undefined &&
          content.hasUserCompleted
        ) {
          completed++
        }
      })
      setCompleted(completed)
      setIsModuleOpen(openOnStart)
    }
  }, [contents])

  async function handleModuleUpdate(value: string) {
    await mutate({
      fetchPolicy: 'no-cache',
      variables: {
        moduleId: module.id,
        input: { title: value },
      },
    })
  }

  const handleModuleContentLink = () => {
    return setCollectionModule({
      collectionId,
      moduleId: module.id,
      visibility,
    })
  }

  function hanldeModuleVisibility() {
    if (!isModuleOpen && !canManage) {
      mixpanelTrack('Modulo esplorato', {
        id_corso: collectionSlug,
        nome_corso: collectionName,
        nome_modulo: title,
      })

      TagManager.dataLayer({
        dataLayer: {
          event: 'tay_collection_module_open',
          moduleName: title,
          collectionSlug: collectionSlug,
        },
      })
    }

    setIsModuleOpen(!isModuleOpen)
  }

  async function removeModule() {
    await remove({
      variables: {
        moduleId: module.id,
      },
    })

    refetchOnUpdate()
  }

  async function removeModuleAndContents() {
    await removeAll({
      variables: {
        moduleId: module.id,
      },
    })

    if (contents) {
      contents.forEach((content) =>
        mixpanelTrack('Rimozione Contenuto', {
          id_corso: collectionId,
          id_contenuto: content.id,
          tipo_contenuto: content.__typename,
        })
      )
    }

    refetchOnUpdate()
  }

  const shouldDisplayDuration = Boolean(
    module.duration && module.duration >= 60
  )

  function handleOnBlur(value) {
    handleModuleUpdate(value)
    setShowLength(false)
  }

  if (loading) {
    return null
  }

  const duration = secondsToDuration(module.duration)

  return (
    <div className={styles.module}>
      <div className={canManage ? styles.wrapperManage : styles.wrapper}>
        <div className="flex flex-col gap-y-1 w-full min-w-0">
          {!!canManage ? (
            <div className="flex flex-col">
              <Input
                className={classNames(
                  'my-auto text-brand-main border-none w-full truncate',
                  styles.noShadow
                )}
                disabled={!canManage}
                onBlur={(e) => handleOnBlur(e.target.value)}
                defaultValue={module.title ?? 'Module'}
                onChange={(e) => setTitle(e.target.value)}
                onFocus={() => setShowLength(true)}
                maxlength={MAXLENGTH}
              />
            </div>
          ) : (
            <Title
              className={` my-auto leading-6 py-auto break-words ${
                completed === contents.length
                  ? 'text-brand-accent2 '
                  : 'text-brand-main'
              }  `}
            >
              {module.title}
            </Title>
          )}
          <div className="flex items-center justify-between">
            <PreTitle className="text-gray-500 font-semibold">
              {completed}/{contents?.length ?? 0}
              {shouldDisplayDuration && duration && ` · ${duration}`}
            </PreTitle>
            <label
              className={`text-right text-gray-500 text-xs ${
                showLength === true ? 'block' : 'hidden'
              }`}
            >
              {title.length}/{MAXLENGTH}
            </label>
          </div>
        </div>
        <div className={`flex ${!canManage && 'mb-3'}`}>
          <Button
            icon={
              <FontAwesomeIcon
                icon={['fas', isModuleOpen ? 'chevron-up' : 'chevron-down']}
                className="text-gray-500 h-5 mt-2.5"
                width={20}
              />
            }
            className="p-0"
            onClick={hanldeModuleVisibility}
            size="S"
            subtype="circle"
          />
          {canManage && (
            <ModuleMenu
              key={`modMenu-${module.id}`}
              onRemove={removeModule}
              onRemoveAll={removeModuleAndContents}
            />
          )}
        </div>
      </div>
      {isModuleOpen &&
        (!canManage ? (
          <div
            className={classNames(
              'flex flex-col gap-y-5',
              module?.isUnlocked === false && 'pointer-events-none opacity-50'
            )}
          >
            {map(contents, (content) => (
              <Item
                key={content.id}
                content={content}
                collectionId={collectionId}
                collectionSlug={collectionSlug}
                canReadCollection={canReadCollection}
                canManageCollection={canManage}
                collectionVisibility={visibility}
                refetchOnUpdate={refetchOnUpdate}
                isModule={true}
              />
            ))}
          </div>
        ) : (
          canManage && (
            <Fragment>
              <SortableWrapper
                collectionId={collectionId}
                contents={contents}
                collectionSlug={collectionSlug}
                canReadCollection={true}
                canManageCollection={canManage}
                refetchOnUpdate={refetchOnUpdate}
                isModule={true}
                moduleId={module?.id}
                collectionVisibility={visibility}
              />
            </Fragment>
          )
        ))}

      {canManage && (
        <div className="flex gap-x-5">
          <Button
            size="S"
            onClick={handleModuleContentLink}
            className="bg-gray-100 text-gray-500 "
            icon={<FontAwesomeIcon icon={['fas', 'plus']} />}
          >
            <Trans>Associa contenuti</Trans>{' '}
          </Button>
          {!featureIsEnabledForThisUser && (
            <CreateContent
              collectionSlug={collectionSlug}
              moduleId={module?.id}
            />
          )}
        </div>
      )}
    </div>
  )
}

export default ModuleInfo
